/**
 * Shared formatting + display helpers for claim data.
 *
 * Extracted here so every component that renders a claim (Workshop, ERA,
 * Client Chart, ClaimEditDialog, version history, dialogs) uses the same
 * status palette and date formatting — previously the Workshop and
 * ClaimEditDialog had drifted `getStatusBadge` implementations, so newly
 * added statuses (`resubmitted`, `acknowledged`, etc.) rendered correctly
 * in one place and as a generic gray chip in the other.
 */

// ---------------------------------------------------------------------------
// Status badge — tailwind class string keyed by claim status.
// Unknown statuses fall back to a neutral gray so the app never crashes when
// a new status ships in the backend ahead of the frontend.
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  claim_created:     'bg-slate-100 text-slate-800',
  ready_for_billing: 'bg-purple-100 text-purple-800',
  ready_to_bill:     'bg-purple-100 text-purple-800', // alias for ready_for_billing
  in_progress:       'bg-indigo-100 text-indigo-800',
  billed:            'bg-blue-100 text-blue-800',
  billed_legacy:     'bg-purple-100 text-purple-800 border border-purple-300',
  submitted:         'bg-blue-100 text-blue-800',
  // Feb 2026 — granular mid-pipeline states surfaced by TriZetto CSRs.
  // clearinghouse_accepted = TriZetto received + is forwarding to payer
  // payer_accepted         = payer's pre-edits passed (strongest pre-pay
  //                          positive signal); adjudication still pending
  clearinghouse_accepted: 'bg-sky-100 text-sky-800 border border-sky-300',
  payer_accepted:    'bg-emerald-100 text-emerald-800 border border-emerald-300',
  resubmitted:       'bg-violet-100 text-violet-800',
  manually_submitted: 'bg-slate-200 text-slate-800 border border-slate-400',
  acknowledged:      'bg-cyan-100 text-cyan-800',
  pending:           'bg-yellow-100 text-yellow-800',
  paid:              'bg-green-100 text-green-800',
  partially_paid:    'bg-emerald-100 text-emerald-800',
  denied:            'bg-red-100 text-red-800',
  // Jun 2026 — level-aware denials: primary paid, downstream payer denied.
  denied_secondary:  'bg-red-100 text-red-800 border border-red-300',
  denied_tertiary:   'bg-red-100 text-red-800 border border-red-400',
  ready_for_tertiary: 'bg-amber-100 text-amber-900 border border-amber-300',
  // Legacy status — consolidated into client_owes (kept for stragglers).
  patient_responsibility: 'bg-teal-100 text-teal-800',
  rejected:          'bg-rose-100 text-rose-800',
  needs_correction:  'bg-amber-100 text-amber-800',
  needs_review:      'bg-orange-100 text-orange-800',
  hold:              'bg-orange-100 text-orange-800',
  client_owes:     'bg-teal-100 text-teal-800',
  closed_denial:     'bg-orange-100 text-orange-700',
  ready_for_secondary: 'bg-amber-100 text-amber-800',
  crossed_to_secondary: 'bg-purple-100 text-purple-800',
  pending_forwarded: 'bg-sky-100 text-sky-800',
  // Payer take-back states (Phase 2 ERA ledger)
  takeback_received:          'bg-rose-100 text-rose-800 border border-rose-300',
  takeback_rebilled_pending:  'bg-orange-100 text-orange-800 border border-orange-300',
  takeback_unresolved:        'bg-red-200 text-red-900 border border-red-400 font-semibold',
  superseded:        'bg-gray-100 text-gray-600',
  voided:            'bg-gray-200 text-gray-700',
  replaced:          'bg-gray-200 text-gray-700',
};

export const getStatusBadge = (status) => {
  // Normalize to lowercase — some legacy/ERA writes stored the status as
  // 'PAID' / 'DENIED' / etc. while the canonical app value is lowercase.
  const key = String(status || '').toLowerCase();
  return STATUS_STYLES[key] || 'bg-gray-100 text-gray-800';
};

/** Pretty-printed human label for a status (snake_case → Title Case). */
// Option A (Jun 2026): claim.status keeps the single generic 'rejected'
// workflow value; when the TriZetto CSR pipeline knows WHO rejected it,
// surface that on the main badge label ("Payer Rejected" / "CH Rejected").
export const getRejectedSourceLabel = (claim) => {
  if (String(claim?.status || '').toLowerCase() !== 'rejected') return null;
  const csr = claim?.csr_last_status;
  if (csr === 'payer_rejected') return 'Payer Rejected';
  if (csr === 'clearinghouse_rejected') return 'CH Rejected';
  return null;
};

export const formatStatusLabel = (status) => {
  // Normalize to lowercase first so legacy uppercase variants are handled.
  const key = String(status || '').toLowerCase();
  if (key === 'billed_legacy') return 'Billed (Legacy)';
  if (key === 'takeback_received') return 'Take-Back — Awaiting Rebill';
  if (key === 'takeback_rebilled_pending') return 'Rebill Pending Adjudication';
  if (key === 'takeback_unresolved') return 'Take-Back Unresolved';
  if (key === 'denied_secondary') return 'Denied - Secondary';
  if (key === 'denied_tertiary') return 'Denied - Tertiary';
  if (key === 'ready_for_tertiary') return 'Ready for Tertiary';
  // Jun 2026 — consolidated: Client Owes is the single patient-balance status.
  if (key === 'patient_responsibility') return 'Client Owes';
  return key
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// ---------------------------------------------------------------------------
// Claim-aware status helpers (Jun 2026) — a secondary (COB) claim that has
// been transmitted keeps the canonical DB status 'submitted' (so ERA posting,
// CSR polling, and status queries stay untouched) but the UI should read
// "Submitted to Secondary" so billers can tell at a glance which payer the
// claim went to.  Detection: generation_role==='secondary' stamped at
// queue-time, or the secondary_submitted_at breadcrumb stamped at transmit.
// ---------------------------------------------------------------------------
export const isSecondarySubmission = (claim) => {
  const key = String(claim?.status || '').toLowerCase();
  return key === 'submitted' &&
    (claim?.generation_role === 'secondary' || Boolean(claim?.secondary_submitted_at));
};

export const formatClaimStatusLabel = (claim) => {
  if (isSecondarySubmission(claim)) return 'Submitted to Secondary';
  return formatStatusLabel(claim?.status);
};

export const getClaimStatusBadge = (claim) => {
  if (isSecondarySubmission(claim)) return 'bg-purple-100 text-purple-800 border border-purple-300';
  return getStatusBadge(claim?.status);
};

// ---------------------------------------------------------------------------
// Date formatting — never let the browser's timezone shift a date-only
// string into the previous day. Accepts either `YYYY-MM-DD` or a full ISO
// timestamp; returns '' on failure.
// ---------------------------------------------------------------------------
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const formatLocalDate = (isoStr, opts = null) => {
  const dt = parseLocalDate(isoStr);
  if (!dt) return isoStr ? String(isoStr) : '';
  return dt.toLocaleDateString(
    undefined,
    opts || { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
  );
};

// ---------------------------------------------------------------------------
// Deadline urgency — shared by ClaimsWorkshop's Deadline cell and the Billing
// tab's BillingDeadlineBadge. Keeps the "color + label" mapping in one place
// so both surfaces always agree on what "14 days left" looks like.
//
// Returns:
//   cls       — tailwind classes for the badge (bg + text + border + weight)
//   label     — human-readable "Due in X days" / "Due TODAY" / "5 days overdue"
//   icon      — lucide icon name hint: 'alert' | 'clock' | null
//   severity  — 'overdue' | 'critical' | 'urgent' | 'warning' | 'caution' | 'ok'
// ---------------------------------------------------------------------------
export const getDeadlineUrgency = (daysRemaining, isPastDeadline = false) => {
  if (daysRemaining === null || daysRemaining === undefined || Number.isNaN(daysRemaining)) {
    return {
      cls: 'text-gray-500 bg-gray-100 border-gray-200',
      label: 'N/A',
      icon: null,
      severity: 'ok',
    };
  }
  if (isPastDeadline || daysRemaining < 0) {
    return {
      cls: 'text-red-700 bg-red-100 border-red-300 font-bold',
      label: `${Math.abs(daysRemaining)} days overdue`,
      icon: 'alert',
      severity: 'overdue',
    };
  }
  if (daysRemaining === 0) {
    return {
      cls: 'text-red-700 bg-red-100 border-red-300 font-bold',
      label: 'Due TODAY',
      icon: 'alert',
      severity: 'critical',
    };
  }
  if (daysRemaining <= 14) {
    return {
      cls: 'text-red-600 bg-red-50 border-red-200 font-semibold',
      label: daysRemaining === 1 ? 'Due in 1 day' : `Due in ${daysRemaining} days`,
      icon: 'clock',
      severity: 'urgent',
    };
  }
  if (daysRemaining <= 29) {
    return {
      cls: 'text-orange-600 bg-orange-50 border-orange-200',
      label: `Due in ${daysRemaining} days`,
      icon: null,
      severity: 'warning',
    };
  }
  if (daysRemaining <= 59) {
    return {
      cls: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      label: `Due in ${daysRemaining} days`,
      icon: null,
      severity: 'caution',
    };
  }
  return {
    cls: 'text-green-700 bg-green-50 border-green-200',
    label: `Due in ${daysRemaining} days`,
    icon: null,
    severity: 'ok',
  };
};
